import {
  type ChipTypeMap,
  type FilterOptionsState,
  type AutocompleteChangeDetails,
  type AutocompleteValue,
  Box,
  createFilterOptions,
  Autocomplete as MuiAutocomplete,
  useTheme,
} from '@mui/material';
import { AutocompleteProps, ComboBoxOption } from './types';
import { t } from '@lingui/macro';
import { useState, useCallback } from 'react';
import { parseSearch } from './ComboBox';
import { useGet } from 'lib/api';
import { debounce, omit } from 'lodash';
import { TextInput, TextInputProps } from './TextInput';
import { colors } from 'styles/theme/colors';
import { Check } from '@phosphor-icons/react/dist/ssr';
import { CaretDown, Warning } from '@phosphor-icons/react';
import { Chip } from 'components/chip';
import { Text } from 'components/text';

export const Autocomplete = <
  Value extends Omit<ComboBoxOption, 'handleClick'>,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
  ChipComponent extends React.ElementType = ChipTypeMap['defaultComponent'],
>({
  sx,
  options = [],
  openText = t`Open`,
  loadingText = t`Loading...`,
  noOptionsText = t`No options`,
  variant = 'unstyled',
  api = null,
  optionsLimit,
  atSearch = false,
  filterOptions: filterOptionsProps,
  onChange,
  onBlur,
  componentsProps,
  optionsChipRender,
  optionsChipProps,
  isInvalidOption,
  ...rest
}: Omit<
  AutocompleteProps<Value, Multiple, DisableClearable, FreeSolo, ChipComponent>,
  'inputVariant' | 'options'
> & {
  options?: Value[];
  name?: string;
  required?: boolean;
  variant?: AutocompleteProps<
    Value,
    Multiple,
    DisableClearable,
    FreeSolo,
    ChipComponent
  >['inputVariant'];
}) => {
  const [search, setSearch] = useState('');
  const theme = useTheme();

  // Helper to resolve vars (can be array or function)
  const getVars = () => {
    if (!api?.vars) return [];
    return typeof api.vars === 'function' ? api.vars() : api.vars;
  };

  const { data } = useGet(
    api?.url || null,
    api?.search ? [api?.key, search] : api?.key || '',
    api?.search ? [parseSearch(atSearch, search), ...getVars()] : [...getVars()],
    undefined,
    api?.revalidate !== false,
    {
      enabled: api?.search ? Boolean(parseSearch(atSearch, search)) : !!api,
      ...(api?.options ?? {}),
    }
  );
  const selectOptions = (data && api ? api.responseTransform(data) : options) as Value[];

  const inputChangeApi = useCallback(
    debounce((newInputValue: string) => {
      setSearch(newInputValue);
    }, 250),
    []
  );

  // limit amount of options shown
  const defaultFilterOptions = createFilterOptions<Value>();
  const filterOptions = (opts: Value[], state: FilterOptionsState<Value>) => {
    if (atSearch) {
      const searchValue = parseSearch(atSearch, state.inputValue);
      if (!searchValue) return [];
      return defaultFilterOptions(opts, { ...state, inputValue: searchValue }).slice(
        0,
        optionsLimit
      );
    }
    return defaultFilterOptions(opts, state).slice(0, optionsLimit);
  };

  return (
    <MuiAutocomplete
      selectOnFocus
      openText={openText}
      loadingText={loadingText}
      onKeyDown={e => {
        if (e.key === 'Enter') {
          e.stopPropagation();
        }
      }}
      noOptionsText={noOptionsText}
      options={(selectOptions ?? []) as Value[]}
      sx={{
        '.MuiInputBase-root': {
          padding: 0,
          input: { padding: '10px 14px' },
        },
        '.MuiFormControl-root': {
          '.MuiInputBase-root': {
            input: { padding: '10px 14px' },
          },
        },
        flex: 1,
        ...sx,
      }}
      filterOptions={
        optionsLimit
          ? (filterOptions as (
              filteredOptions: Value[],
              state: FilterOptionsState<Value>
            ) => Value[])
          : filterOptionsProps
      }
      onInputChange={(event, newInputValue, reason) => {
        rest?.onInputChange?.(event, newInputValue, reason);
        if (api?.search) inputChangeApi(newInputValue);
      }}
      renderOption={(optionProps, option) => {
        const isInvalid = isInvalidOption?.(option);
        const textColor = isInvalid ? theme.palette.text.disabled : theme.palette.text.secondary;

        return (
          <Box
            component="li"
            {...optionProps}
            sx={{
              display: 'flex',
              width: '100%',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              flexDirection: 'row',
              padding: '8px 10px',
              gap: '8px',
              marginBlock: '1px',
              borderRadius: '4px',
              color: `${textColor} !important`,
              fill: `${textColor} !important`,
              '& span, & div': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              },
              ...(isInvalid ? { '& .MuiAvatar-root': { opacity: 0.5 } } : {}),
              '&.MuiAutocomplete-option[aria-selected="true"]': {
                background: colors.brand[50],
                fontWeight: 600,
                '& span, & div': {
                  fontWeight: 600,
                },
              },
            }}
            key={option?.id}
          >
            {isInvalid && (
              <Warning color="inherit" size={24} style={{ minWidth: '24px', minHeight: '24px' }} />
            )}
            {/* {rest.multiple ? <Checkbox checked={Boolean(optionProps['aria-selected'])} /> : null} */}
            {option.CustomElement ? (
              <option.CustomElement label={option.label} {...option.customProps} />
            ) : (
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {option.label}
              </span>
            )}
            {(optionsChipRender || optionsChipProps?.label) && (
              <Chip
                isTooltip={false}
                size="sm"
                label={optionsChipRender?.(option)}
                {...omit(optionsChipProps, ['labelProps'])}
                sx={themeSX => {
                  return {
                    marginLeft: 'auto',
                    ...optionsChipProps?.sxWithOption?.(themeSX, option),
                  };
                }}
              />
            )}
            {optionProps?.['aria-selected'] && (
              <Check
                size={20}
                style={{
                  minWidth: '20px',
                  minHeight: '20px',
                  ...(!(optionsChipRender || optionsChipProps?.label)
                    ? { marginLeft: 'auto' }
                    : {}),
                }}
                color={colors.brand[400]}
              />
            )}
          </Box>
        );
      }}
      popupIcon={<CaretDown size={20} />}
      {...rest}
      componentsProps={{
        ...componentsProps,
        paper: {
          ...(componentsProps?.paper ?? {}),
          sx: {
            ...((selectOptions?.length > 0 || !rest?.freeSolo) && {
              marginTop: '8px',
              minWidth: 250,
              borderRadius: '8px',
              border: `0.5px solid ${colors.grey[200]}`,
              boxShadow:
                '0px 12px 16px -4px rgba(16, 24, 40, 0.08), 0px 4px 6px -2px rgba(16, 24, 40, 0.03)',
              ul: {
                padding: '6px',
              },
            }),
            ...(componentsProps?.paper?.sx ?? {}),
          },
        },
      }}
      onChange={(event, value, reason, details) => {
        onChange?.(
          event,
          (value ?? '') as AutocompleteValue<Value, Multiple, DisableClearable, FreeSolo>,
          reason,
          details as AutocompleteChangeDetails<Value>
        );
      }}
      onBlur={event => {
        onBlur?.(event);
      }}
      renderInput={params => {
        const textParams = {
          ...params,
          InputLabelProps: { ...params.InputLabelProps, shrink: true, required: rest.required },
        };
        return (
          <TextInput
            {...(omit(rest, [
              'value',
              'name',
              'onBlur',
              'onChange',
              'data-testid',
            ]) as TextInputProps)}
            {...textParams}
            variant={variant}
            InputProps={{ ...params.InputProps, ...(rest.InputProps ?? {}) }}
          />
        );
      }}
    />
  );
};
